import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import SubscriptionCatalogItem from './SubscriptionCatalogItem';
import { usePayerAssignments } from '../hooks/usePayerAssignments';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { getProduct } from '../subscriptions/catalog';
import { getProductIcon } from '../subscriptions/entitlementIcons';
import { normalizeEntitlementsMap, ownsProduct } from '../subscriptions/entitlements';
import { getNextStep, PURCHASE_STEP_ORDER } from '../subscriptions/purchaseSteps';
import { assignmentOnboardingPath } from '../utils/onboarding';

export interface SubscriptionPurchaseOptionsProps {
  /** Post-checkout return path. Defaults to the current location pathname. */
  returnTo?: string;
}

const SubscriptionPurchaseOptions: React.FC<SubscriptionPurchaseOptionsProps> = ({
  returnTo: returnToProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { assignments, loading, error, refetch } = usePayerAssignments();
  const {
    confirmCheckout,
    loading: checkoutLoading,
    error: confirmError,
    clearError: clearConfirmError,
  } = useSubscriptionCheckout();
  const confirmAttempted = useRef<string | null>(null);

  const returnTo = returnToProp ?? location.pathname;

  const initialAssignmentId = searchParams.get('assignment_id');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | ''>('');
  const [checkoutNotice, setCheckoutNotice] = useState<{
    severity: 'success' | 'error' | 'info';
    message: string;
    instruction?: string;
    retryable?: boolean;
  } | null>(null);

  const checkoutStatus = searchParams.get('checkout');
  const sessionId = searchParams.get('session_id');

  const activeAssignmentId = useMemo(() => {
    if (selectedAssignmentId !== '') {
      return selectedAssignmentId;
    }
    if (initialAssignmentId) {
      const parsed = parseInt(initialAssignmentId, 10);
      if (!Number.isNaN(parsed) && assignments.some((a) => a.id === parsed)) {
        return parsed;
      }
    }
    return assignments.length === 1 ? assignments[0]?.id : undefined;
  }, [assignments, initialAssignmentId, selectedAssignmentId]);

  const activeAssignment = assignments.find((a) => a.id === activeAssignmentId);
  const entitlements = normalizeEntitlementsMap(activeAssignment?.entitlements);
  const nextStep = getNextStep(entitlements);
  const activeStepIndex = nextStep
    ? PURCHASE_STEP_ORDER.indexOf(nextStep)
    : PURCHASE_STEP_ORDER.length;

  const clearCheckoutQuery = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('session_id');
    next.delete('return_to');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const runConfirm = useCallback(
    async (stripeSessionId: string) => {
      clearConfirmError();
      const result = await confirmCheckout(stripeSessionId);
      clearCheckoutQuery();

      if (result) {
        await refetch();
        setCheckoutNotice({
          severity: 'success',
          message: result.alreadyFulfilled
            ? `${result.productName} is already active on your assignment.`
            : `Purchase complete — ${result.productName} is now unlocked.`,
        });
        // Assignment-less checkouts create a brand-new assignment; route into
        // onboarding so setup can continue from whatever page hosted checkout.
        navigate(assignmentOnboardingPath(result.assignmentId), {
          replace: true,
          state: {
            checkoutSuccess: result.productName,
            alreadyFulfilled: result.alreadyFulfilled,
          },
        });
      }
    },
    [clearCheckoutQuery, clearConfirmError, confirmCheckout, navigate, refetch],
  );

  useEffect(() => {
    if (checkoutStatus === 'cancelled') {
      setCheckoutNotice({
        severity: 'info',
        message: 'Checkout was cancelled.',
      });
      clearCheckoutQuery();
    }
  }, [checkoutStatus, clearCheckoutQuery]);

  useEffect(() => {
    if (checkoutStatus !== 'success' || !sessionId || confirmAttempted.current === sessionId) {
      return;
    }
    confirmAttempted.current = sessionId;
    void runConfirm(sessionId);
  }, [checkoutStatus, sessionId, runConfirm]);

  useEffect(() => {
    if (confirmError) {
      setCheckoutNotice({
        severity: 'error',
        message: confirmError.message,
        instruction: confirmError.instruction,
        retryable: confirmError.retryable,
      });
    }
  }, [confirmError]);

  const handleRetryConfirm = () => {
    if (!sessionId) return;
    setCheckoutNotice(null);
    confirmAttempted.current = null;
    void runConfirm(sessionId);
  };

  return (
    <Box data-testid="subscription-purchase-options">
      {(checkoutLoading || (checkoutStatus === 'success' && sessionId)) && (
        <Box sx={{ mb: 1.5 }} data-testid="checkout-confirming">
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Confirming your purchase…
          </Typography>
        </Box>
      )}

      {checkoutNotice && (
        <Alert
          severity={checkoutNotice.severity}
          sx={{ mb: 1.5, py: 0 }}
          data-testid="checkout-notice"
          data-attr={`checkout-notice--${checkoutNotice.severity}`}
          action={
            checkoutNotice.retryable && sessionId ? (
              <Button color="inherit" size="small" onClick={handleRetryConfirm}>
                Try again
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2">{checkoutNotice.message}</Typography>
          {checkoutNotice.instruction && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {checkoutNotice.instruction}
            </Typography>
          )}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
          {error}
        </Alert>
      )}

      {!loading && assignments.length > 1 && (
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel id="subscription-assignment-select">Purchase for assignment (optional)</InputLabel>
          <Select
            labelId="subscription-assignment-select"
            label="Purchase for assignment (optional)"
            value={activeAssignmentId ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedAssignmentId(value === '' ? '' : Number(value));
            }}
          >
            <MenuItem value="">
              <em>New assignment</em>
            </MenuItem>
            {assignments.map((assignment) => (
              <MenuItem key={assignment.id} value={assignment.id}>
                Assignment #{assignment.id}
                {assignment.athlete?.str ? ` — ${assignment.athlete.str}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!loading && assignments.length === 1 && activeAssignment && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Purchasing for assignment #{activeAssignment.id}
          {activeAssignment.athlete?.str ? ` (${activeAssignment.athlete.str})` : ''}.
        </Typography>
      )}

      {!loading && assignments.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Your purchase will create a new empty assignment for you to send invites after.
        </Typography>
      )}

      {!loading && (
        <Stepper
          activeStep={activeStepIndex}
          orientation="vertical"
          data-testid="subscription-stepper"
          sx={{
            '& .MuiStep-root': {
              py: 0,
            },
            '& .MuiStepLabel-root': {
              py: 0.25,
            },
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.25,
            },
            '& .MuiStepIcon-root': {
              width: 22,
              height: 22,
              fontSize: '0.8rem',
            },
            '& .MuiStepIcon-root.Mui-completed': {
              width: 22,
              height: 22,
            },
            '& .MuiStepConnector-line': {
              minHeight: 6,
            },
            '& .MuiStepContent-root': {
              ml: '11px',
              pl: 1.75,
              pt: 0,
              pb: 0,
              borderLeftWidth: 1,
            },
          }}
        >
          {PURCHASE_STEP_ORDER.map((productId) => {
            const product = getProduct(productId);
            if (!product) {
              return null;
            }

            const owned = ownsProduct(entitlements, productId);
            const isNext = productId === nextStep;
            const Icon = getProductIcon(productId);

            return (
              <Step
                key={productId}
                completed={owned}
                expanded={!owned}
                active={isNext}
                data-testid={`subscription-step--${productId}`}
              >
                <StepLabel icon={owned ? undefined : <Icon fontSize="small" />}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {product.name}
                    {isNext && productId !== 'starter' && (
                      <Chip
                        label="Up next"
                        size="small"
                        color="secondary"
                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </Box>
                </StepLabel>
                {!owned && (
                  <StepContent>
                    <SubscriptionCatalogItem
                      productId={productId}
                      assignmentId={activeAssignmentId}
                      entitlements={entitlements}
                      returnTo={returnTo}
                      disabled={checkoutLoading}
                      hideTitle
                      emphasized={isNext}
                      compact
                    />
                  </StepContent>
                )}
              </Step>
            );
          })}
        </Stepper>
      )}

      {!ownsProduct(entitlements, 'full_access') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Or get everything at once
          </Typography>
          <SubscriptionCatalogItem
            productId="full_access"
            assignmentId={activeAssignmentId}
            entitlements={entitlements}
            returnTo={returnTo}
            disabled={checkoutLoading}
          />
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionPurchaseOptions;
